import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, api, Badge, Button, Input, ListPage, Modal, Select, useFetch, useForm, usePagedList } from '@greenrinse/ui';
import type { Country } from './CountriesPage';

export interface Partner {
  id: string; countryId: string; countryCode: string; name: string; legalName: string | null; taxId: string | null; billingAddress: string | null;
  billingEmail: string | null; contactName: string | null; contactPhone: string | null; isActive: boolean; siteCount: number; vehicleCount: number;
  activeContractCount: number; createdAtUtc: string;
}

const empty = { countryId: '', name: '', legalName: '', taxId: '', billingAddress: '', billingEmail: '', contactName: '', contactPhone: '', isActive: true };

export function PartnerForm({ partner, countries, onSaved, onCancel }: { partner: Partner | null; countries: Country[]; onSaved: (p: Partner) => void; onCancel: () => void }) {
  const form = useForm<typeof empty>(partner ? {
    countryId: partner.countryId, name: partner.name, legalName: partner.legalName ?? '', taxId: partner.taxId ?? '', billingAddress: partner.billingAddress ?? '',
    billingEmail: partner.billingEmail ?? '', contactName: partner.contactName ?? '', contactPhone: partner.contactPhone ?? '', isActive: partner.isActive,
  } : { ...empty, countryId: countries[0]?.id ?? '' });

  const save = () => form.submit(async (v) => {
    const body = { ...v, legalName: v.legalName || null, taxId: v.taxId || null, billingAddress: v.billingAddress || null, billingEmail: v.billingEmail || null, contactName: v.contactName || null, contactPhone: v.contactPhone || null };
    const saved = partner ? await api<Partner>(`/api/partners/${partner.id}`, { method: 'PUT', body }) : await api<Partner>('/api/partners', { method: 'POST', body });
    onSaved(saved);
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
      {form.message && <div className="gr-span-2" style={{ marginBottom: 12 }}><Alert tone="error">{form.message}</Alert></div>}
      <Input label="Company name" {...form.bind('name')} />
      <Select label="Country" {...form.bind('countryId')} options={countries.map((c) => ({ value: c.id, label: `${c.name} (${c.currency})` }))} />
      <Input label="Legal name" {...form.bind('legalName')} />
      <Input label="Tax ID / TRN" {...form.bind('taxId')} />
      <div className="gr-span-2"><Input label="Billing address" {...form.bind('billingAddress')} /></div>
      <Input label="Billing email" type="email" {...form.bind('billingEmail')} />
      <Input label="Contact name" {...form.bind('contactName')} />
      <Input label="Contact phone" {...form.bind('contactPhone')} />
      <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>
      <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={form.busy}>Save</Button>
      </div>
    </form>
  );
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [countryId, setCountryId] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const countries = useFetch<Country[]>('/api/countries');
  const list = usePagedList<Partner>('/api/partners', { countryId: countryId || undefined, includeInactive });
  const [creating, setCreating] = useState(false);

  return (
    <>
      <ListPage<Partner>
        title="Partners" state={list} rowKey={(p) => p.id} onRowClick={(p) => navigate(`/partners/${p.id}`)}
        actions={<Button onClick={() => setCreating(true)}>Add partner</Button>}
        filters={
          <>
            <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={countryId} onChange={(e) => setCountryId(e.target.value)}>
              <option value="">All countries</option>
              {(countries.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="gr-row"><input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /> Show inactive</label>
          </>
        }
        columns={[
          { key: 'name', header: 'Partner', render: (p) => <><strong>{p.name}</strong>{p.legalName && <div className="gr-muted" style={{ fontSize: 13 }}>{p.legalName}</div>}</> },
          { key: 'country', header: 'Country', render: (p) => p.countryCode, width: 80 },
          { key: 'sites', header: 'Sites', render: (p) => p.siteCount, align: 'end' },
          { key: 'vehicles', header: 'Vehicles', render: (p) => p.vehicleCount, align: 'end' },
          { key: 'contracts', header: 'Active contracts', render: (p) => p.activeContractCount, align: 'end' },
          { key: 'status', header: '', render: (p) => p.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
        ]}
      />
      <Modal open={creating} title="New partner" onClose={() => setCreating(false)}>
        <PartnerForm partner={null} countries={countries.data ?? []} onCancel={() => setCreating(false)} onSaved={(p) => { setCreating(false); navigate(`/partners/${p.id}`); }} />
      </Modal>
    </>
  );
}
